import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';

import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezone from 'dayjs/plugin/timezone';

import { saveAs } from 'file-saver';

import EChart from '../chart/echarts/EChart';
import { tooltipRow } from '../chart/echarts/tooltipAdapter';
import NoData from '../NoData';
import { buildExecutionPlanOption, FailedPlanPoint, FAILED_PLANS_SERIES_NAME, planTimestamp, PLANS_SERIES_NAME, PlanOverlay, PlanPoint, SELECTED_COLOUR } from '../chart/echarts/executionPlanOption';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import ExecutionPlan from '../../types/instance/ExecutionPlan';
import CockroachPlan from '../instance/plan/CockroachPlan';
import Db2Plan from '../instance/plan/Db2Plan';
import MongoPlan from '../instance/plan/MongoPlan';
import MySqlPlan from '../instance/plan/MySqlPlan';
import OraclePlan from '../instance/plan/OraclePlan';
import PostgresPlan from '../instance/plan/PostgresPlan';
import SapHanaPlan from '../instance/plan/SapHanaPlan';
import SqlBatchPlan from '../instance/plan/SqlBatchPlan';
import SybasePlan from '../instance/plan/SybasePlan';
import GenericPlan from '../instance/plan/GenericPlan';
import Statement from "../../types/instance/Statement";
import CombinedChanges from "../../types/instance/CombinedChanges";
import ChartPlotLine from "../../types/ChartPlotLine";
import Time from "../../types/instance/Time";

// Constants.
import {
    INSTANCE_TYPE_COCKROACHDB,
    INSTANCE_TYPE_DB2,
	INSTANCE_TYPE_INFORMIX,
    INSTANCE_TYPE_MONGODB,
    INSTANCE_TYPE_MYSQL,
    INSTANCE_TYPE_ORACLE,
    INSTANCE_TYPE_POSTGRES,
    INSTANCE_TYPE_SAPHANA,
    INSTANCE_TYPE_SQLSERVER,
    INSTANCE_TYPE_SYBASE,
    COLOUR_INSTANCE_EXECUTIONS,
    COLOUR_INSTANCE_TIME
} from '../Constants';

// Helpers.
import { getConvertedTimeToUTC, getTimeZone } from '../../helpers/utils';
import Helper from "../../helpers/Helper";
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { buildApiQuery, buildRedirectQuery } from "../../helpers/timeRangeUtils";
import { useEventTypes } from "../../context/EventTypeContext";
import ConditionalRender from "../../helpers/ConditionalRender";
import { getAPIString, isGenericStatement, StatementTypeInfo } from "../../views/instances/tabs/utils";
import ChatButton from "../chatGPT/ChatButton";
import Prompts from "../chatGPT/Prompts";
import archiver from "../../api/Archiver";
import EventsApi from "../../api/instance/EventsApi";

dayjs.extend(utc.default);
dayjs.extend(timezone.default);

const COST_KEYS_DB2 = ['total_cost'];
const COST_KEYS_MYSQL = ['query_cost'];
const COST_KEYS_ORACLE = ['cost'];
const COST_KEYS_POSTGRES = ['Total Cost'];
const COST_KEYS_SAPHANA = ['subtree_cost'];
const COST_KEYS_ANY = ['Total Cost', 'total_cost', 'query_cost', 'subtree_cost', 'cost'];
const COST_SEARCH_MAX_DEPTH = 8;

const firstNumber = (...values: any[]): number | undefined => {
    for (const value of values) {
        if (value === undefined || value === null || value === '') continue;
        const numeric = Number(value);
        if (!Number.isNaN(numeric)) return numeric;
    }
    return undefined;
};

const findCostValue = (node: any, keys: string[], depth: number = 0): number | undefined => {
    if (!node || typeof node !== 'object' || depth > COST_SEARCH_MAX_DEPTH) return undefined;

    for (const key of keys) {
        const value = firstNumber(node[key]);
        if (value !== undefined) return value;
    }

    for (const child of Object.values(node)) {
        const value = findCostValue(child, keys, depth + 1);
        if (value !== undefined) return value;
    }

    return undefined;
};

const getSqlServerCost = (sqlplan: string): number | undefined => {
    const doc = new DOMParser().parseFromString(sqlplan, 'application/xml');
    if (doc.getElementsByTagNameNS('*', 'parsererror').length) return undefined;

    for (const tag of ['StmtSimple', 'StmtCursor', 'StmtCond', 'StmtUseDb']) {
        const cost = firstNumber(doc.getElementsByTagNameNS('*', tag)[0]?.getAttribute('StatementSubTreeCost'));
        if (cost !== undefined) return cost;
    }

    return firstNumber(doc.getElementsByTagNameNS('*', 'RelOp')[0]?.getAttribute('EstimatedTotalSubtreeCost'));
};

// Components.
function ExecutionPlans(props: { instance: InstanceTarget, batchId?: string, statementId?: string, statement?: Statement, statementType?: StatementTypeInfo }) {
    const [executionPlans, setExecutionPlans] = useState<ExecutionPlan[]>([]);
    const [failedPlans, setFailedPlans] = useState<ExecutionPlan[]>([]);
    const [executionPlan, setExecutionPlan] = useState<ExecutionPlan>();
    const [combinedChanges, setCombinedChanges] = useState<CombinedChanges[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [showChangeMarkers, setShowChangeMarkers] = useState<boolean>(true);
    const [showCustomEventMarkers, setShowCustomEventMarkers] = useState<boolean>(true);
    const [showAlertMarkers, setShowAlertMarkers] = useState<boolean>(true);
    const [showTotalTimeOverlay, setShowTotalTimeOverlay] = useState<boolean>(false);
    const [showExecutionsOverlay, setShowExecutionsOverlay] = useState<boolean>(false);
    const [showAvgDurationOverlay, setShowAvgDurationOverlay] = useState<boolean>(false);
    const [totalTimeSeries, setTotalTimeSeries] = useState<number[][]>([]);
    const [executionsSeries, setExecutionsSeries] = useState<number[][]>([]);
    const [avgDurationSeries, setAvgDurationSeries] = useState<number[][]>([]);
    const [previousInstanceId, setPreviousInstanceId] = useState<number | undefined>(props.instance.id);
    const timeRangeContext = useContext(TimeRangeContext)
    const { eventTypes } = useEventTypes();
    const { instance } = props;
    const overlayButtonRef = useRef<HTMLButtonElement>(null);

    // Reset stale plan state synchronously when the instance changes, so children
    // never render a plan from a different instance type (e.g. SQL Server XML
    // momentarily passed to MySqlPlan, which would crash JSON.parse).
    if (previousInstanceId !== instance.id) {
        setPreviousInstanceId(instance.id);
        setExecutionPlan(undefined);
        setExecutionPlans([]);
        setFailedPlans([]);
        setLoading(true);
    }

    useEffect(() => {
        const button = overlayButtonRef.current;
        if (!button) return;

        const onShow = () => {
            const panel = button.closest('.p-splitter-panel') as HTMLElement;
            if (panel) panel.style.minHeight = '350px';
        };

        const onHide = () => {
            const panel = button.closest('.p-splitter-panel') as HTMLElement;
            if (panel) panel.style.minHeight = '';
        };

        button.addEventListener('show.bs.dropdown', onShow);
        button.addEventListener('hidden.bs.dropdown', onHide);

        return () => {
            button.removeEventListener('show.bs.dropdown', onShow);
            button.removeEventListener('hidden.bs.dropdown', onHide);
        };
    }, [executionPlans.length]);

    const hasObjectStatistics = Boolean(
            instance?.collectobjectstatistics && instance?.hasobjectstatistics
    );
    const formatCost = (value: number): string =>
            new Intl.NumberFormat('en-GB', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 6
            }).format(value);

    const normalizePoint = (point: any): [number, number] => {
        if (Array.isArray(point)) {
            return [Number(point[0]) || 0, Number(point[1]) || 0];
        }
        return [Number(point?.x) || 0, Number(point?.y) || 0];
    };

    const fillMissingTimeslices = (data: number[][]): number[][] => {
        const from = timeRangeContext.timeRange?.from;
        const to = timeRangeContext.timeRange?.to;
        if (!from || !to) {
            return [...data].sort((a, b) => a[0] - b[0]);
        }

        const interval = Helper.getInterval(from, to, timeRangeContext.timeRange?.interval);
        const start = Math.floor(from.valueOf() / interval) * interval;
        const end = Math.ceil(to.valueOf() / interval) * interval;

        const valueByTimeslice = new Map<number, number>();
        data.forEach((point: any) => {
            const [x, y] = normalizePoint(point);
            const roundedX = Math.floor(x / interval) * interval;
            valueByTimeslice.set(roundedX, y);
        });

        const filledData: number[][] = [];
        for (let timeslice = start; timeslice <= end; timeslice += interval) {
            filledData.push([timeslice, valueByTimeslice.get(timeslice) ?? 0]);
        }
        return filledData;
    };

    useEffect(() => {
        let isActive = true;

        function getTotalCost(sqlplan: string): number | undefined {
            try {
                if (instance.type === INSTANCE_TYPE_SQLSERVER) {
                    return getSqlServerCost(sqlplan);
                }

                const plan = JSON.parse(sqlplan);
                const root = Array.isArray(plan) ? plan[0] : plan;

                switch (instance.type) {
                    case INSTANCE_TYPE_DB2:
                        return firstNumber(root?.['total_cost'], findCostValue(plan, COST_KEYS_DB2));

                    case INSTANCE_TYPE_MYSQL:
                        return firstNumber(root?.['query_block']?.['cost_info']?.['query_cost'],
                                findCostValue(plan, COST_KEYS_MYSQL));

                    case INSTANCE_TYPE_ORACLE:
                        return firstNumber(root?.['cost'], findCostValue(plan, COST_KEYS_ORACLE));

                    case INSTANCE_TYPE_POSTGRES:
                        return firstNumber(root?.['Plan']?.['Total Cost'], findCostValue(plan, COST_KEYS_POSTGRES));

                    case INSTANCE_TYPE_SAPHANA:
                        return firstNumber(root?.['subtree_cost'], findCostValue(plan, COST_KEYS_SAPHANA));

                    case INSTANCE_TYPE_COCKROACHDB:
                    case INSTANCE_TYPE_MONGODB:
                    case INSTANCE_TYPE_INFORMIX:
                    case INSTANCE_TYPE_SYBASE:
                        return undefined;

                    default:
                        return findCostValue(plan, COST_KEYS_ANY);
                }
            } catch (error: any) {
                return undefined;
            }
        }

        const processExecutionPlans = (plans: ExecutionPlan[]) => {
            if (!isActive) return;

            for (let index = 0; index < plans.length; index++) {

                let executionPlan = plans[index],
                        cost: number | undefined,
                        valid: boolean = executionPlan.sqlplan !== null;
                const tz = getTimeZone();
                const descriptor = tz === 'UTC'
                        ? dayjs.utc(executionPlan.timeslice).format('DD MMM YYYY HH:mm')
                        : dayjs.default.tz(executionPlan.timeslice, tz).format('DD MMM YYYY HH:mm');

                // Try to find the execution plan cost - this can be hit-and-miss, and varies between instance types.
                if (valid) {
                    cost = getTotalCost(executionPlan.sqlplan);
                }

                // Update the execution plan options.
                executionPlan.planId = index;
                executionPlan.descriptor = descriptor;
                executionPlan.cost = cost;
                executionPlan.valid = valid;

                plans[index] = executionPlan;
            }

            const failed = plans.filter(plan => !plan.valid && plan.descriptor && plan.message);

            // Filter out plans with no description or plan to explain.
            plans = plans.filter(plan => plan.valid && plan.descriptor && plan.sqlplan !== null);

            if (isActive) {
                setExecutionPlans(plans);
                setFailedPlans(failed);
                setLoading(false)

                // Always reflect the current fetch, even when it found nothing - otherwise a plan
                // selected for a previous time range keeps rendering after the range changes.
                setExecutionPlan(plans.length > 0 ? plans[0] : undefined);
            }
        }

        const getExecutionPlans = async () => {

            let planFormat: string = '&format=json';

            const apiString = getAPIString(props.statementId || props.batchId, undefined, props.statementType)

            if (props.batchId === undefined && props.statementId === undefined) {
                console.error('Execution plan component requires either a batch or statement ID property to be set.')
                return;
            }

            if (instance.type === INSTANCE_TYPE_SQLSERVER) {
                planFormat = '&format=xml'
            }

            archiver.get(`/rest/v2/sql/plan?${buildApiQuery(timeRangeContext.timeRange)}&sort=timeslice&id=${instance.id}${apiString}${planFormat}`)
                    .then(async (response: { data: ExecutionPlan[]; }) => {
                        if (!isActive) return;
                        let plans: ExecutionPlan[] = response.data;

                        // if it's statement search check also for group plans (DBMAR-1657)
                        const isGeneric = isGenericStatement()
                        if (!plans.length && isGeneric) {
                            // Make another API call with the "grouphash" parameter
                            archiver.get(`/rest/v2/sql/plan?${buildApiQuery(timeRangeContext.timeRange)}&sort=timeslice&id=${instance.id}&grouphash=${props.statementId}${planFormat}`)
                                    .then(async (response: { data: ExecutionPlan[]; }) => {
                                        if (!isActive) return;
                                        plans = response.data;
                                        processExecutionPlans(plans);
                                        setLoading(false);
                                    })
                                    .catch((error: any) => {
                                        if (!isActive) return;
                                        console.error('Failed to retrieve V2 execution plans with grouphash.', error);
                                        setLoading(false);
                                    });
                        } else {
                            processExecutionPlans(plans);
                            setLoading(false);
                        }
                    })
                    .catch((error: any) => {
                        if (!isActive) return;
                        console.error('Failed to retrieve V2 execution plans.', error);
                        setLoading(false);
                    });
        }

        getExecutionPlans();

        return () => {
            isActive = false;
        };
    }, [instance, props.batchId, props.statementId, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    useEffect(() => {
        let isActive = true;

        const getChanges = async () => {
            const allEvents = await EventsApi.getEventsChangesAndAlerts(timeRangeContext, instance.id);
            if (!isActive || !allEvents) return;

            const updatedCombinedChanges: CombinedChanges[] = allEvents.map((currentItem: any, index: number) => ({
                id: index,
                instanceId: currentItem.datasourceid,
                timeslice: currentItem.timeslice,
                [currentItem.event]: currentItem
            }));

            updatedCombinedChanges.sort(function compare(a, b) {
                const aa: number = dayjs.default(a.timeslice).valueOf();
                const bb: number = dayjs.default(b.timeslice).valueOf();
                return bb - aa;
            });

            if (isActive) {
                setCombinedChanges(updatedCombinedChanges);
            }
        };

        void getChanges();

        return () => {
            isActive = false;
        };
    }, [instance.id, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    useEffect(() => {
        let isActive = true;

        const getOverlayData = async () => {
            const apiString = getAPIString(props.statementId || props.batchId, undefined, props.statementType);
            const queryString = buildApiQuery(timeRangeContext.timeRange);
            const limit = 501;

            const [timeResult, statisticsResult] = await Promise.allSettled([
                archiver.get(`/rest/v1/activity/time?${queryString}&id=${instance.id}${apiString}&interval=0&limit=${limit}&sort=waittime+desc`),
                archiver.get(`/rest/v2/sql/statistic/time?${queryString}&id=${instance.id}&type=${instance.type}${apiString}&interval=0&limit=${limit}&sort=executions+desc`)
            ]);

            if (!isActive) return;

            if (timeResult.status === 'fulfilled') {
                const totalTimeData: number[][] = (timeResult.value.data as Time[]).map((item: Time) => ([
                    getConvertedTimeToUTC(item),
                    Number(item.waittime) || 0
                ]));
                setTotalTimeSeries(fillMissingTimeslices(totalTimeData));
            } else {
                setTotalTimeSeries([]);
            }

            if (statisticsResult.status === 'fulfilled') {
                const sqlStatisticTime = statisticsResult.value.data || [];

                const executionsData: number[][] = sqlStatisticTime.map((item: any) => ([
                    getConvertedTimeToUTC(item),
                    Number(item.executions) || 0
                ]));

                const avgDurationData: number[][] = sqlStatisticTime.map((item: any) => ([
                    getConvertedTimeToUTC(item),
                    Number(
                            item.avgduration ??
                            ((item.executions || 0) > 0 ? (item.duration || 0) / item.executions : 0)
                    ) || 0
                ]));

                setExecutionsSeries(fillMissingTimeslices(executionsData));
                setAvgDurationSeries(fillMissingTimeslices(avgDurationData));
            } else {
                setExecutionsSeries([]);
                setAvgDurationSeries([]);
            }
        };

        void getOverlayData();

        return () => {
            isActive = false;
        };
    }, [instance.id, instance.type, props.statementId, props.batchId, props.statementType, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval]);

    const chartPlotLines = useMemo((): ChartPlotLine[] => {
        const interval = Helper.getInterval(
                timeRangeContext.timeRange?.from,
                timeRangeContext.timeRange?.to,
                timeRangeContext.timeRange?.interval
        );
        const chartCategories = Helper.getChartAxisCategories(
                timeRangeContext.timeRange?.from,
                timeRangeContext.timeRange?.to,
                interval
        );

        const filteredCombinedChanges = combinedChanges.filter(item => {
            if (item.change && !showChangeMarkers) return false;
            if (item.custom && !showCustomEventMarkers) return false;
            if (item.alert && !showAlertMarkers) return false;
            return true;
        });

        const eventId = props.statementId || (props.batchId ? `${props.statementType?.type || 'batch'}:${props.batchId}` : undefined);
        return Helper.getChartPlotLines(
                buildRedirectQuery(timeRangeContext.timeRange),
                filteredCombinedChanges,
                chartCategories,
                interval,
                false,
                eventId,
                eventTypes
        );
    }, [
        combinedChanges,
        showChangeMarkers,
        showCustomEventMarkers,
        showAlertMarkers,
        timeRangeContext,
        props.statementId,
        props.batchId,
        props.statementType,
        eventTypes
    ]);

    function selectPreviousPlan() {
        if (currentPlanIndex > 0) {
            setExecutionPlan(executionPlans[currentPlanIndex - 1]);
        }
    }

    function selectNextPlan() {
        if (currentPlanIndex !== -1 &&
                currentPlanIndex < executionPlans.length - 1) {
            setExecutionPlan(executionPlans[currentPlanIndex + 1]);
        }
    }

    function selectPreviousPlanEnabled() {
        return currentPlanIndex > 0;
    }

    function selectNextPlanEnabled() {
        return currentPlanIndex !== -1 &&
                currentPlanIndex < executionPlans.length - 1;
    }

    const currentPlanIndex = executionPlan ?
            executionPlans.findIndex(plan => plan.planId === executionPlan.planId) : -1;

    function planContext(plan?: ExecutionPlan) {
        const database = plan?.database
                ? <> for the database <span className="fw-bold">{plan.database}</span></>
                : null;
        const username = plan?.username
                ? <> {database ? 'and' : 'for'} username <span className="fw-bold">{plan.username}</span></>
                : null;

        return <>{database}{username}</>;
    }

    function showPlan() {
        // Keep the compiler happy: the execution plan should not be null here.
        if (!executionPlan) return (<></>);

        switch (instance.type) {
            case INSTANCE_TYPE_COCKROACHDB:
                return (<CockroachPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_DB2:
                return (<Db2Plan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_MONGODB:
                return (<MongoPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_MYSQL:
                return (<MySqlPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_ORACLE:
                return (<OraclePlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_POSTGRES:
                return (<PostgresPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SAPHANA:
                return (<SapHanaPlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SQLSERVER:
                return (<SqlBatchPlan executionPlan={executionPlan}/>);

            case INSTANCE_TYPE_SYBASE:
                return (<SybasePlan hasObjectStatistics={hasObjectStatistics} executionPlan={executionPlan}/>);

            default:
                return (<GenericPlan executionPlan={executionPlan}/>);
        }
    }

    const overlays = useMemo<PlanOverlay[]>(() => {
        const list: PlanOverlay[] = [];
        if (showTotalTimeOverlay) {
            list.push({ name: 'Total Time', chartType: 'bar', color: COLOUR_INSTANCE_TIME, yAxisIndex: 1,
                data: totalTimeSeries.map((point: any) => normalizePoint(point)) });
        }
        if (showExecutionsOverlay) {
            list.push({ name: 'Executions', chartType: 'line', color: COLOUR_INSTANCE_EXECUTIONS, yAxisIndex: 2,
                data: executionsSeries.map((point: any) => normalizePoint(point)) });
        }
        if (showAvgDurationOverlay) {
            list.push({ name: 'Avg Duration', chartType: 'line', color: '#03bafc', yAxisIndex: 3,
                data: avgDurationSeries.map((point: any) => normalizePoint(point)) });
        }
        return list;
    }, [showTotalTimeOverlay, showExecutionsOverlay, showAvgDurationOverlay, totalTimeSeries, executionsSeries, avgDurationSeries]);

    const planTooltip = useCallback((params: any) => {
        const timezone = getTimeZone();
        const timestamp = Array.isArray(params.value) ? params.value[0] : params.value;
        const header = planTimestamp(timestamp, timezone);
        let rows = '';

        if (params.seriesName === FAILED_PLANS_SERIES_NAME) {
            const message = String(params.data?.message ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            return `<div class="chart-tooltip-new"><div class="tooltip-header">${header}</div><div style="color:#dc3545">${message}</div></div>`;
        }

        if (params.seriesName === PLANS_SERIES_NAME) {
            const plan = params.data?.plan;
            const cost = plan && plan.cost !== undefined && plan.cost !== null ? formatCost(plan.cost) : '0';
            rows += `<div style="margin-top:4px">${tooltipRow(SELECTED_COLOUR, 'Cost', cost)}</div>`;
        } else {
            overlays.forEach(overlay => {
                const point = overlay.data.find(entry => entry[0] === timestamp);
                if (!point) {
                    return;
                }
                if (overlay.name === 'Executions') {
                    rows += tooltipRow(overlay.color, overlay.name, Helper.getFormattedNumber(point[1]));
                } else if (point[1] > 0) {
                    rows += tooltipRow(overlay.color, overlay.name, Helper.getTimeInEnglish(point[1]));
                }
            });

            const plansAtX = executionPlans.filter(plan => getConvertedTimeToUTC(plan) === timestamp);
            if (plansAtX.length) {
                rows += `<div style="margin-top:4px;border-top:1px solid #eee;padding-top:4px">`;
                plansAtX.forEach(plan => {
                    const cost = plan.cost !== undefined && plan.cost !== null ? formatCost(plan.cost) : '0';
                    rows += tooltipRow(SELECTED_COLOUR, 'Cost', cost);
                });
                rows += `</div>`;
            }
        }

        if (!rows) {
            return '';
        }
        return `<div class="chart-tooltip-new"><div class="tooltip-header">${header}</div>${rows}</div>`;
    }, [overlays, executionPlans]);

    const chartOption = useMemo(() => {
        const rangeFrom = timeRangeContext.timeRange.from;
        const rangeTo = timeRangeContext.timeRange.to;
        const rangeInterval = Helper.getInterval(rangeFrom, rangeTo, timeRangeContext.timeRange.interval);

        const plans: PlanPoint[] = executionPlans.map(plan => {
            const costNum = plan.cost !== undefined && plan.cost !== null ? Number(plan.cost) : NaN;
            return {
                x: getConvertedTimeToUTC(plan),
                y: Number.isFinite(costNum) ? costNum : 1,
                plan,
                selected: !!executionPlan && plan.planId === executionPlan.planId,
            };
        });

        const failed: FailedPlanPoint[] = failedPlans.map(plan => ({
            x: getConvertedTimeToUTC(plan),
            plan,
            message: String(plan.message ?? ''),
            selected: !!executionPlan && plan.planId === executionPlan.planId,
        }));

        return buildExecutionPlanOption({
            plans,
            failedPlans: failed,
            overlays,
            plotLines: chartPlotLines,
            min: rangeFrom ? Math.floor(dayjs.default(rangeFrom).valueOf() / rangeInterval) * rangeInterval : undefined,
            max: rangeTo ? Math.ceil(dayjs.default(rangeTo).valueOf() / rangeInterval) * rangeInterval : undefined,
            timezone: getTimeZone(),
            tooltipFormatter: planTooltip,
        });
    }, [executionPlans, failedPlans, executionPlan, overlays, chartPlotLines, planTooltip,
        timeRangeContext.timeRange.from, timeRangeContext.timeRange.to, timeRangeContext.timeRange.interval]);

    const onChartClick = useCallback((params: any) => {
        if (params?.data?.plan) {
            setExecutionPlan(params.data.plan);
        }
    }, []);

    // Function to trigger the json download
    function downloadJson() {
        const isJSONFormat = (value: any) => {
            try {
                JSON.parse(value);
                return true;
            } catch (error) {
                return false;
            }
        };
        if (executionPlan) {
            const isJSON = isJSONFormat(executionPlan.sqlplan);
            if (isJSON) {
                const blob = new Blob([executionPlan.sqlplan], { type: 'application/json' });
                saveAs(blob, `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.json`);
            } else {
                // Display the text content with a monospaced font in a <pre> tag
                const executionPlanText = executionPlan.sqlplan
                const preTag = document.createElement('pre');

                // Not sure why we need styling for a txt file... https://appperf.atlassian.net/browse/DBMAR-1536
                preTag.style.fontSize = '14px';
                preTag.style.fontFamily = 'Courier New, Courier, monospace';
                preTag.style.display = 'none';
                preTag.textContent = executionPlanText;
                document.body.appendChild(preTag);

                const blob = new Blob([executionPlanText], { type: 'text/plain;charset=utf-8' });
                saveAs(blob, `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.txt`);
            }
        }
    }

    function downloadSqlServerPlan() {
        if (!executionPlan?.sqlplan) return;

        const blob = new Blob([executionPlan.sqlplan], { type: 'application/xml;charset=utf-8' });
        saveAs(
            blob,
            `${instance.name} - ${((props.batchId !== undefined) ? props.batchId : props.statementId)} - Execution Plan - ${executionPlan.descriptor}.sqlplan`
        );
    }

    return (
            <div id="plans" className="tab-pane" role="tabpanel" aria-labelledby="plans-tab">
                <div>
                    <div className="card-header sticky-card-header">
                        <div className="d-flex flex-column flex-md-row flex-md-wrap align-items-stretch align-items-md-center gap-2 w-100">
                            <div className="d-flex align-items-center flex-wrap gap-2 flex-grow-1 min-w-0">
                                <i className="fal fa-ruler-triangle fa-fw" aria-hidden="true"/>
                                <span className="me-1">Execution Plans</span>
                                {executionPlans.length > 1 && (
                                        <div className="btn-group">
                                            <button
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary"
                                                    onClick={selectPreviousPlan}
                                                    disabled={!selectPreviousPlanEnabled()}
                                                    title="Previous Plan"
                                            >
                                                <i className="fal fa-arrow-left"/>
                                            </button>
                                            <button
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary"
                                                    onClick={selectNextPlan}
                                                    disabled={!selectNextPlanEnabled()}
                                                    title="Next Plan"
                                            >
                                                <i className="fal fa-arrow-right"/>
                                            </button>
                                        </div>
                                )}
                                {executionPlans.length > 0 && (
                                        <div className="btn-group">
                                            <button
                                                    ref={overlayButtonRef}
                                                    id="executionPlanOverlay"
                                                    type="button"
                                                    className="btn btn-xsm btn-outline-secondary dropdown-toggle"
                                                    data-bs-toggle="dropdown"
                                                    data-bs-auto-close="outside"
                                                    data-bs-boundary="viewport"
                                                    aria-expanded="false"
                                            >
                                                Overlay
                                            </button>
                                            <ul className="dropdown-menu border" aria-labelledby="executionPlanOverlay" style={{ userSelect: 'none' }}>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowChangeMarkers(!showChangeMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showChangeMarkers ? '' : 'invisible'}`}/>
                                                Changes
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowCustomEventMarkers(!showCustomEventMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showCustomEventMarkers ? '' : 'invisible'}`}/>
                                                Custom events
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowAlertMarkers(!showAlertMarkers)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showAlertMarkers ? '' : 'invisible'}`}/>
                                                Alerts
                                            </span>
                                                </li>
                                                <li>
                                                    <hr className="dropdown-divider"/>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowTotalTimeOverlay(!showTotalTimeOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showTotalTimeOverlay ? '' : 'invisible'}`}/>
                                                Total Time
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowExecutionsOverlay(!showExecutionsOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showExecutionsOverlay ? '' : 'invisible'}`}/>
                                                Executions
                                            </span>
                                                </li>
                                                <li>
                                            <span className="dropdown-item" role="button" onClick={() => setShowAvgDurationOverlay(!showAvgDurationOverlay)}>
                                                <i className={`fal fa-check fa-fw me-1 ${showAvgDurationOverlay ? '' : 'invisible'}`}/>
                                                Avg Duration
                                            </span>
                                                </li>
                                            </ul>
                                        </div>
                                )}
                                <i className="collapse-toggle ms-auto" role="button" data-bs-toggle="collapse"
                                   data-bs-target="#collapsePlans" aria-expanded="false" aria-controls="collapsePlans"/>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-center ms-md-auto" role="group">
                                <ConditionalRender if={props.statement && executionPlan?.sqlplan}>
                                    {/*@ts-ignore*/}
                                    <ChatButton value={Prompts.getStatementTextPrompt(props.statement, executionPlan?.sqlplan?.toString(), instance.type)}/>
                                </ConditionalRender>
                                {executionPlan && executionPlan.valid && (
                                    <>
                                        <ConditionalRender if={instance.type === INSTANCE_TYPE_SQLSERVER}>
                                            <button type="button" onClick={downloadSqlServerPlan} className="btn btn-xsm btn-primary">
                                                Download Plan
                                            </button>
                                        </ConditionalRender>
                                        <ConditionalRender if={instance.type !== INSTANCE_TYPE_SQLSERVER}>
                                            <button type="button" onClick={downloadJson} className="btn btn-xsm btn-primary">
                                                Download Plan
                                            </button>
                                        </ConditionalRender>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div id="collapsePlans" className="card-body collapse show">
                        <ConditionalRender if={loading}>
                            <div className="w-100 text-center text-muted mt-3">
                                <div className="loader spinner chartSpinner">
                                </div>
                                <p className="mt-3">
                                    Loading data...
                                </p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={!loading && executionPlan === undefined && failedPlans.length === 0}>
                            <div className="w-100 text-center text-muted my-3">
                                <i className="fal fa-ban fa-fw fa-2x text-muted"/>
                                <p>No Execution Plans Found</p>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading && (executionPlans.length > 0 || failedPlans.length > 0)}>
                            <div style={{ marginBottom: '20px', position: 'relative', zIndex: 0 }}>
                                <div style={{ paddingLeft: '10px' }}>
                                    <h6 style={{ margin: '0 0 5px 0' }}>Execution plans</h6>
                                    <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>
                                        Select one of the dots to see the execution plan.
                                    </p>
                                </div>
                                <div className="execution-plan-samples-chart">
                                    {!loading && (executionPlans.length > 0 || failedPlans.length > 0)
                                        ? <EChart option={chartOption} height={200} onClick={onChartClick}
                                                  plotLines={chartPlotLines}/>
                                        : <NoData error={null} loading={loading}
                                                  length={executionPlans.length + failedPlans.length}/>}
                                </div>
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading && executionPlan && executionPlan.valid}>
                            <div className='execution-plan'>
                                <p>The execution plan below is for <span
                                        className="fw-bold">{executionPlan?.descriptor}</span>{planContext(executionPlan)}.
                                </p>
                                {showPlan()}
                            </div>
                        </ConditionalRender>
                        <ConditionalRender if={!loading && executionPlan && !executionPlan.valid}>
                            <div className='execution-plan'>
                                <p>Attempt to get execution plan at <span
                                        className="fw-bold">{executionPlan?.descriptor}</span>{planContext(executionPlan)} failed.
                                </p>
                                <p className="text-danger">{executionPlan?.message}</p>
                            </div>
                        </ConditionalRender>
                    </div>
                </div>
            </div>
    );
}

export default ExecutionPlans;
